import { useEffect, useRef, type MouseEvent } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  AI_CHAT_TOGGLE_EVENT,
  CHAT_OPEN_STATE_KEY,
} from "@/components/specific/ai/chatConstants";
import { useAiChatSession } from "@/components/specific/ai/useAiChatSession";
import { useConfirmNewChat } from "@/components/specific/ai/ConfirmNewChat";
import {
  ChatGreeting,
  ChatHeader,
  ChatInput,
  ChatMessages,
  ChatPrompts,
  ChatRestoring,
  JumpToLatestButton,
} from "@/components/specific/ai/AiChatView";
import { ChatLengthStatus, chatLengthIndicator } from "@/components/specific/ai/ChatLengthStatus";

export const AskVectaPage = () => {
  const { updateCacheItem } = useLocalStorage();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const session = useAiChatSession({ active: true, pageScroll: true });
  const isEmpty = session.messages.length === 0;
  const { requestNewChat, confirmDialog } = useConfirmNewChat({
    hasMessages: session.hasMessages,
    onNewChat: session.handleNewChat,
  });

  useEffect(() => {
    updateCacheItem<boolean>(CHAT_OPEN_STATE_KEY, false);
  }, [updateCacheItem]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const focusInput = () => textareaRef.current?.focus();
    window.addEventListener(AI_CHAT_TOGGLE_EVENT, focusInput);
    return () => window.removeEventListener(AI_CHAT_TOGGLE_EVENT, focusInput);
  }, []);

  const handleLinkClick = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest("a[href]");
    const staysOnPage = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (!link || staysOnPage || link.getAttribute("target") === "_blank") return;
    updateCacheItem<boolean>(CHAT_OPEN_STATE_KEY, true);
  };

  return (
    <div onClickCapture={handleLinkClick} className="flex flex-1 flex-col bg-[#F7F8F9] text-slate-800">
      <div className="sticky top-0 z-10 bg-[#F7F8F9]">
        <ChatHeader onNewChat={requestNewChat} />
      </div>
      <div
        className={`mx-auto flex w-full max-w-4xl flex-1 flex-col px-4 ${
          isEmpty ? "justify-center gap-4 pb-5" : "pt-3"
        }`}
      >
        {isEmpty && (session.isLoadingHistory ? <ChatRestoring /> : <ChatGreeting />)}
        {!isEmpty && (
          <ChatMessages
            pageScroll
            messages={session.messages}
            scrollRef={session.scrollRef}
            onScroll={session.handleMessagesScroll}
            showJumpToLatest={false}
            onJumpToLatest={session.scrollToLatest}
            onPromptSelect={session.handleChatChanged}
            isSending={session.isSending}
            onRegenerate={session.handleRegenerate}
            onRetry={session.handleRetry}
            editableMessageId={session.editableMessageId}
            onEditLast={() => {
              session.handleEditLast();
              textareaRef.current?.focus();
            }}
          />
        )}
        <div className={isEmpty ? "" : "sticky bottom-0 z-10 mt-auto bg-[#F7F8F9] pb-5"}>
          {!isEmpty && session.showJumpToLatest && (
            <JumpToLatestButton
              onClick={session.scrollToLatest}
              className="absolute -top-9 left-1/2 -translate-x-1/2"
            />
          )}
          <ChatLengthStatus
            length={session.length}
            isSending={session.isSending}
            onCompact={session.handleCompact}
            onNewChat={requestNewChat}
          />
          <ChatInput
            limitReached={session.length.limitReached}
            lengthIndicator={chatLengthIndicator(session.length)}
            textareaRef={textareaRef}
            value={session.messageText}
            onChange={session.handleChatChanged}
            onSend={session.handleSend}
            onStop={session.handleStop}
            isSending={session.isSending}
            onNewChat={requestNewChat}
            editing={session.editing}
            onCancelEdit={session.cancelEdit}
            onEditLast={session.editableMessageId === null ? undefined : session.handleEditLast}
            disabled={!session.messageText.trim() || session.isSending || session.isLoadingHistory}
            variant={isEmpty ? "hero" : "docked"}
          />
        </div>
        {isEmpty && !session.isLoadingHistory && <ChatPrompts onPromptSelect={session.handleChatChanged} />}
      </div>
      {confirmDialog}
      <div role="status" aria-live="polite" className="sr-only">
        {session.announcement}
      </div>
    </div>
  );
};
